import { useEffect } from 'react';
import styles from './styles.module.scss';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useReactFlow,
} from 'reactflow';
import { deleteFlowWatermark } from './helper';

import 'reactflow/dist/style.css';
import { useNodesFromTransactions } from '@starsoft/common/hooks/transactions/useNodesFromTransactions';
import { ErrorCard } from '@starsoft/common/components/ErrorCard';

/**
 * TransactionsCommonMappingModalContentBoard Component
 * This component is responsible for rendering a visual representation of transaction nodes and edges using ReactFlow.
 * It handles the display of nodes and edges, error handling, and view adjustments.
 *
 * @returns {JSX.Element} The rendered component displaying the transaction flow or an error card if an error occurs.
 *
 * @example
 * // Usage example:
 * <TransactionsCommonMappingModalContentBoard />
 */
export function TransactionsCommonMappingModalContentBoard() {
  // Destructure necessary data and functions from the useNodesFromTransactions hook
  const {
    edges, // Array of edge objects representing connections between nodes
    nodes, // Array of node objects representing transaction entities
    onEdgesChange, // Function to handle changes to edges
    onNodesChange, // Function to handle changes to nodes
    transactionByIdQuery: { error, refetch }, // Error and refetch function from the transaction query
  } = useNodesFromTransactions();

  // Destructure the fitView function from the useReactFlow hook
  const { fitView } = useReactFlow();

  /**
   * handleAdjustNodeOnEnter Function
   * This function removes the React Flow watermark and adjusts the view to fit the nodes.
   * It is executed when the component mounts.
   */
  function handleAdjustNodeOnEnter() {
    // Remove the React Flow watermark
    deleteFlowWatermark();

    // Adjust the view to fit the nodes with a duration of 200ms
    setTimeout(() => fitView({ duration: 200 }), 200);
  }

  // useEffect hook to execute handleAdjustNodeOnEnter when the component mounts
  useEffect(handleAdjustNodeOnEnter, [handleAdjustNodeOnEnter]);

  // If there is an error, render the ErrorCard component with the error and refetch function
  if (error) {
    return (
      <div className={styles.container}>
        <ErrorCard error={error} refetch={refetch} />
      </div>
    );
  }

  // Render the ReactFlow component with nodes, edges, and additional controls
  return (
    <div className={styles.container}>
      <ReactFlow
        className="intersection-flow"
        deleteKeyCode={null} // Disable the delete key functionality
        selectionKeyCode={null} // Disable the selection key functionality
        nodes={nodes} // Pass the nodes to the ReactFlow component
        edges={edges} // Pass the edges to the ReactFlow component
        onNodesChange={onNodesChange} // Handle node changes
        onEdgesChange={onEdgesChange} // Handle edge changes
        fitView // Enable the fit view functionality
      >
        <MiniMap position="bottom-right" />{' '}
        {/* Display a mini map at the bottom-right */}
        <Controls position="top-right" />{' '}
        {/* Display controls at the top-right */}
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />{' '}
        {/* Set the background to a dotted pattern */}
      </ReactFlow>
    </div>
  );
}
